import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { plansApi } from "../services/plan";
import { Plan } from "@dto/Plan";
import { PlanItem, PlanItemMinimal } from "@dto/PlanItem";
import { CookieKeys, setCookie } from "@lib/cookies";
import { isNil } from "@lib/isNil";
import { StartPoint } from "@domain/StartPoint";
import { ItemDirection } from "@domain/ItemDirection";
import { DraggableFlatListItem } from "@modules/entities/PlanItemList/helpers";
import { logInfo } from "@lib/logger";
import { getLocalStorageItem } from "@lib/storage";

type PlanView = "list" | "edit" | "navigate";
const HAS_PROMPTED_ADD_DATES_KEY = "hasPromptedAddDates";
const START_POINTS_KEY = "startPoints";

interface PlanItemParentCollection {
  id: number;
  type: "Tour" | "Goal";
  planItems: { entityId: number; entityType: "Place" | "Event" }[];
}

const PARENT_COLLECTION_KEY = "planItemParentCollections";

const getPlanItemParentCollections = () => {
  let result: PlanItemParentCollection[] = [];
  try {
    const parsed = JSON.parse(
      localStorage.getItem(PARENT_COLLECTION_KEY) ?? "[]",
    );
    result = Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    logInfo(error);
  }

  return result;
};

/**
 * Remove a parent collection.
 * @param id The id of the parent collection.
 * @param type The type of the parent collection.
 */
const removeParentCollection = (id: number, type: "Tour" | "Goal") => {
  const parentCollections = getPlanItemParentCollections();
  const index = parentCollections.findIndex(
    (x) => x.id === id && x.type === type,
  );
  if (index > -1) {
    parentCollections.splice(index, 1);
  }
  localStorage.setItem(
    PARENT_COLLECTION_KEY,
    JSON.stringify(parentCollections),
  );
};

/**
 * Add a parent collection.
 * @param id The id of the parent collection.
 * @param type The type of the parent collection.
 * @param entities The entities belonging to the parent collection.
 */
const addParentCollection = (
  id: number,
  type: "Tour" | "Goal",
  entities: { entityId: number; entityType: "Place" | "Event" }[],
) => {
  const parentCollections = getPlanItemParentCollections();
  const index = parentCollections.findIndex(
    (x) => x.id === id && x.type === type,
  );
  if (index > -1) {
    parentCollections.splice(index, 1);
  }
  parentCollections.push({ id, type, planItems: entities });
  localStorage.setItem(
    PARENT_COLLECTION_KEY,
    JSON.stringify(parentCollections),
  );
};

/**
 * Remove a plan item from all parent collections it is a part of.
 * @param entity The entity to remove.
 * @returns The ids and types of the parent collections that now have 0 items left.
 */
const removePlanItemFromParentCollection = (entity: {
  entityId: number;
  entityType: "Place" | "Event";
}): { id: number; type: "Tour" | "Goal" }[] => {
  const parentCollections = getPlanItemParentCollections();
  const collectionsWithEntity = parentCollections.filter((x) =>
    x.planItems.some((y) => y.entityId === entity.entityId),
  );

  collectionsWithEntity.forEach((collection) => {
    const index = collection.planItems.findIndex(
      (x) =>
        x.entityId === entity.entityId && x.entityType === entity.entityType,
    );
    if (index > -1) {
      collection.planItems.splice(index, 1);
    }
  });

  localStorage.setItem(
    PARENT_COLLECTION_KEY,
    JSON.stringify(parentCollections),
  );

  const emptiedCollections = collectionsWithEntity.filter(
    (x) => x.planItems.length === 0,
  );
  emptiedCollections.forEach((collection) => {
    removeParentCollection(collection.id, collection.type);
  });

  return emptiedCollections.map((x) => ({ id: x.id, type: x.type }));
};

interface PlanState {
  /**
   * Whether the bottom sheet for selecting dates or number of days is open.
   */
  isDayPickerOpen: number;
  /**
   * Whether the bottom sheet for selecting the specific day/date to add plan item to is open.
   */
  isAddToDayPickerOpen: number;
  /**
   * Details about the entity to add when addToDay or day pickers are open.
   */
  dayPicker?: {
    entityId: number;
    entityType: "Place" | "Event" | "Tour" | "Challenge";
  };
  // "null" is unscheduled, "all" is show all, and number is a specific day
  selectedDayValue: null | "all" | number;
  startingLocationPicker?: { dayIndex: number | null };
  /**
   * Whether the user has been prompted to add day/dates, so they are not continually asked.
   */
  hasPromptedAddDates: boolean;
  plan?: Plan;
  planItems: PlanItem[];
  startPoints: StartPoint[];
  directions: ItemDirection[];
  /**
   * Track number of times entity added to plan. Used to determine when/if to prompt user
   * to login after configurable number of additions (typically 3).
   */
  numberOfPlanItemsAdded: number;
  planView: PlanView;
  /**
   * Plan items that are being edited. Used by edit screen and map.
   */
  planItemsEditing: DraggableFlatListItem[];
  /**
   * The tours/goals that are in the plan. For state of add/remove buttons for tours and goals.
   */
  parentCollectionsInPlan: { id: number; type: "Tour" | "Goal" }[];
}

export const initialState: PlanState = {
  isDayPickerOpen: -1,
  isAddToDayPickerOpen: -1,
  selectedDayValue: "all",
  hasPromptedAddDates: getLocalStorageItem(HAS_PROMPTED_ADD_DATES_KEY, false),
  planItems: [],
  startPoints: getLocalStorageItem(START_POINTS_KEY, []),
  directions: [],
  numberOfPlanItemsAdded: 0,
  planView: "list",
  planItemsEditing: [],
  parentCollectionsInPlan: getLocalStorageItem(PARENT_COLLECTION_KEY, []).map(
    (x) => ({ id: x.id, type: x.type }),
  ),
};

export const planSlice = createSlice({
  name: "plan",
  initialState,
  reducers: {
    setHasPromptedAddDates: (
      state: PlanState,
      action: PayloadAction<boolean>,
    ) => {
      state.hasPromptedAddDates = action.payload;
      localStorage.setItem(
        HAS_PROMPTED_ADD_DATES_KEY,
        JSON.stringify(action.payload),
      );
    },
    addStartPoint: (state: PlanState, action: PayloadAction<StartPoint>) => {
      state.startPoints.push(action.payload);
      localStorage.setItem("startPoints", JSON.stringify(state.startPoints));
    },
    setPlanAction: (
      state: PlanState,
      action: PayloadAction<{
        plan: Plan;
        planItems: PlanItem[];
      }>,
    ) => {
      state.plan = action.payload.plan;
      state.planItems = action.payload.planItems ?? [];
    },
    setPlanViewAction: (state: PlanState, action: PayloadAction<PlanView>) => {
      state.planView = action.payload;
    },
    setPlanItemsEditingAction: (
      state: PlanState,
      action: PayloadAction<DraggableFlatListItem[]>,
    ) => {
      state.planItemsEditing = action.payload;
    },
    setSelectedDayValue: (
      state: PlanState,
      action: PayloadAction<null | "all" | number>,
    ) => {
      state.selectedDayValue = action.payload;
    },
    removeStartPoint: (state: PlanState, action: PayloadAction<StartPoint>) => {
      const index = state.startPoints.findIndex(
        (x) => x.id === action.payload.id,
      );
      state.startPoints.splice(index, 1);
      state.startPoints = [...state.startPoints];
      localStorage.setItem("startPoints", JSON.stringify(state.startPoints));
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      plansApi.endpoints.createPlan.matchFulfilled,
      (state, action) => {
        void setCookie(CookieKeys.planId, action.payload.id.toString());
        state.plan = action.payload;
      },
    );
    builder.addMatcher(
      plansApi.endpoints.addPlanItem.matchFulfilled,
      (state) => {
        state.numberOfPlanItemsAdded++;
      },
    );
    builder.addMatcher(
      plansApi.endpoints.removePlanItem.matchFulfilled,
      (state, action) => {
        const planItem = state.planItems.find(
          (x) => x.id === action.meta.arg.originalArgs.planItemId,
        );
        if (planItem) {
          // update durable storage
          const removed = removePlanItemFromParentCollection({
            entityId: planItem.plannableId,
            entityType: planItem.plannableType,
          });
          // update redux state
          state.parentCollectionsInPlan = state.parentCollectionsInPlan.filter(
            (x) => !removed.some((y) => y.id === x.id && y.type === x.type),
          );
        }
      },
    );
    builder.addMatcher(
      plansApi.endpoints.addTour.matchFulfilled,
      (state, action) => {
        // update redux state
        state.parentCollectionsInPlan.push({
          id: action.meta.arg.originalArgs.tourId,
          type: "Tour",
        });
        // update durable storage
        addParentCollection(
          action.meta.arg.originalArgs.tourId,
          "Tour",
          action.payload.map((x) => ({
            entityId: x.plannableId,
            entityType: x.plannableType,
          })),
        );
        state.numberOfPlanItemsAdded++;
      },
    );
    builder.addMatcher(
      plansApi.endpoints.removeTour.matchFulfilled,
      (state, action) => {
        // update redux state
        state.parentCollectionsInPlan = state.parentCollectionsInPlan.filter(
          (x) => x.id !== action.meta.arg.originalArgs.tourId,
        );
        // update durable storage
        removeParentCollection(action.meta.arg.originalArgs.tourId, "Tour");
      },
    );
    builder.addMatcher(
      plansApi.endpoints.getPlan.matchFulfilled,
      (state, action) => {
        state.planItems = action.payload.planItems;
        state.plan = action.payload;
        const planCopy = { ...action.payload } as Plan & {
          planItems?: PlanItem[];
        };

        delete planCopy.planItems;
      },
    );
    builder.addMatcher(
      (action) => action.type === "session/signOutAction",
      (state) => {
        for (const key in state) {
          if (state.hasOwnProperty(key)) {
            state[key] = initialState[key];
          }
        }
      },
    );
  },
});

// Selectors
export const selectPlanItems = createSelector(
  (state: RootState) => state.plan,
  (plan) => {
    return plan.planItems;
  },
);
export const selectPlanItemsWithLocations = createSelector(
  (state: RootState) => state.plan,
  (plan) => {
    return plan.planItems.filter((item) => {
      return (
        !isNil(item.plannable.location?.latitude) &&
        !isNil(item.plannable.location?.longitude)
      );
    });
  },
);
export const selectPlan = (state: RootState) => {
  return state.plan.plan;
};
export const selectPlanId = (state: RootState) => {
  return state.plan.plan?.id;
};
export const selectPlanItemByEntity =
  (entityId: number, entityType: string) => (state: RootState) => {
    return state.plan.planItems.find(
      (x) => x.plannableId === entityId && x.plannableType === entityType,
    );
  };
export const selectPlanView = (state: RootState) => {
  return state.plan.planView;
};
export const selectPlanShareUrl = (state: RootState) => {
  return `${window.location.origin}/plan?plan_id=${state.plan.plan?.id}`;
};
export const selectPlanItemsEditing = (state: RootState) => {
  return state.plan.planItemsEditing;
};

export const selectSelectedDayValue = (state: RootState) => {
  return state.plan.selectedDayValue;
};
/**
 * Select if Tour or Goal is in the plan.
 */
export const selectIsCollectionInPlan =
  (
    id: number,
    type: "Event" | "Place" | "Goal" | "Tour" | "Challenge" | "SelfGuided",
  ) =>
  (state: RootState) => {
    return state.plan.parentCollectionsInPlan.some(
      (x) => x.id === id && x.type === type,
    );
  };

/**
 * Select if multi day plan is for number of days or date range or none if not yet selected.
 */
export const selectPlanDayDateType = (state: RootState) => {
  const plan = state.plan.plan;
  if (!plan || plan.dayDuration === null) {
    return "none";
  }

  return plan.startsOn && plan.endsOn ? "dates" : "days";
};
/**
 * Select the starting points for the plan.
 */
export const selectStartingPoints = (state: RootState) => {
  return state.plan.startPoints;
};
/**
 * Select the starting point for day index.
 */
export const selectStartingPointForDayIndex =
  (dayIndex: number | null) => (state: RootState) => {
    return state.plan.startPoints.find((x) => x.dayIndex === dayIndex);
  };
/**
 * Check if there are any plan items for a given day index.
 */
export const selectHasPlanItemsForDay =
  (dayIndex: number | null) => (state: RootState) => {
    return state.plan.planItems.findIndex((x) => x.dayIndex === dayIndex) > -1;
  };
/**
 * Select the plan items for a given day index.
 */
export const selectPlanItemsForDay = createSelector(
  [(_state: RootState, dayIndex: number | null) => dayIndex, selectPlanItems],
  (dayIndex, planItems) => {
    return planItems.filter((x) => x.dayIndex === dayIndex);
  },
);
export const selectHasPromptedAddDates = (state: RootState) => {
  return state.plan.hasPromptedAddDates;
};
/**
 * Sort plan items by day index and position.
 */
export const planItemComparer = (
  a: PlanItem | PlanItemMinimal,
  b: PlanItem | PlanItemMinimal,
) => {
  if (a.dayIndex === b.dayIndex) {
    return a.position < b.position ? -1 : a.position > b.position ? 1 : 0;
  }

  if (a.dayIndex === null) {
    return 1;
  }

  if (b.dayIndex === null) {
    return -1;
  }

  return a.dayIndex < b.dayIndex ? -1 : 1;
};

export const {
  setPlanAction,
  setHasPromptedAddDates,
  setPlanViewAction,
  setPlanItemsEditingAction,
  setSelectedDayValue,
  removeStartPoint,
  addStartPoint,
} = planSlice.actions;

// eslint-disable-next-line import/no-default-export
export default planSlice.reducer;
export type { PlanState, PlanView };
